import { css, StyleSheet } from 'aphrodite';
import moment from 'moment';
import { type FormEvent, useState } from 'react';
import { useUserSession } from '../../../../../../../models/hooks/appStateHooks';
import { DEFAULT_REQUEST_FILE_LINK_EXPIRATION_DAYS, useCreateFileRequestMutation } from '../../../../../../../queries';
import { Button } from '../../../../../../components-v2/Button';
import { DateInput } from '../../../../../../components-v2/DateInput';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../../../components-v2/Dialog';
import { Field, FieldDescription, FieldError, FieldLabel } from '../../../../../../components-v2/Field';
import { Input } from '../../../../../../components-v2/Input';
import { TextArea } from '../../../../../../components-v2/TextArea';
import { SPACE } from '../../../../../../components-v2/Theme/theme';
import { userFacingErrorMessage } from '../userFacingErrorMessage';

export const MAX_DESCRIPTION_LENGTH = 1000;

const DATE_FORMAT = 'YYYY-MM-DD';

const styleSheet = StyleSheet.create({
	form: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[16],
	},
});

interface IProps {
	contactId: string;
	onClose: () => void;
	onCreated: (link: string) => void;
	open: boolean;
}

export const RequestFilesDialog = ({ contactId, onClose, onCreated, open }: IProps) => {
	const userSession = useUserSession();
	const expirationDays =
		userSession.account?.features?.files?.defaultRequestFileLinkExpirationDays ??
		DEFAULT_REQUEST_FILE_LINK_EXPIRATION_DAYS;
	const today = moment().format(DATE_FORMAT);
	const defaultAcceptsUploadsUntil = moment().add(expirationDays, 'days').format(DATE_FORMAT);

	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [acceptsUploadsUntil, setAcceptsUploadsUntil] = useState(defaultAcceptsUploadsUntil);
	const [showNameError, setShowNameError] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const createFileRequest = useCreateFileRequestMutation();

	const reset = () => {
		setName('');
		setDescription('');
		setAcceptsUploadsUntil(defaultAcceptsUploadsUntil);
		setShowNameError(false);
		setSubmitError(null);
	};

	const close = () => {
		if (createFileRequest.isPending) {
			return;
		}
		reset();
		onClose();
	};

	const isDateInPast = !!acceptsUploadsUntil && acceptsUploadsUntil < today;

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		if (!name.trim()) {
			setShowNameError(true);
			return;
		}
		if (isDateInPast) {
			return;
		}
		setSubmitError(null);
		// Uploads stay open through the whole chosen day in the advisor's time zone.
		const expirationDate = acceptsUploadsUntil
			? moment(acceptsUploadsUntil, DATE_FORMAT).endOf('day').toISOString()
			: undefined;
		createFileRequest.mutate(
			{ contactId, description, expirationDate, name },
			{
				onError: error =>
					setSubmitError(userFacingErrorMessage(error, 'Could not create the file request. Try again.')),
				onSuccess: link => {
					if (!link) {
						setSubmitError('Could not create the file request. Try again.');
						return;
					}
					reset();
					onCreated(link);
				},
			}
		);
	};

	return (
		<Dialog open={open} onOpenChange={next => !next && close()}>
			<DialogPopup>
				<form onSubmit={handleSubmit} noValidate>
					<DialogHeader>
						<DialogTitle>Request files</DialogTitle>
						<DialogDescription>
							Tell this contact what you need. They’ll see it on the secure upload page.
						</DialogDescription>
					</DialogHeader>
					<DialogBody>
						<div className={css(styleSheet.form)}>
							<Field>
								<FieldLabel htmlFor='file-request-name'>What are you requesting?</FieldLabel>
								<Input
									aria-invalid={(showNameError && !name.trim()) || undefined}
									autoFocus
									id='file-request-name'
									onChange={e => {
										setName(e.target.value);
										setShowNameError(false);
									}}
									placeholder='e.g. 2025 W-2'
									value={name}
								/>
								{showNameError && !name.trim() ? <FieldError>Add a title for this request.</FieldError> : null}
							</Field>
							<Field>
								<FieldLabel htmlFor='file-request-description'>Details (optional)</FieldLabel>
								<TextArea
									id='file-request-description'
									maxLength={MAX_DESCRIPTION_LENGTH}
									onChange={e => setDescription(e.target.value)}
									placeholder='Anything that helps them send the right documents'
									rows={4}
									value={description}
								/>
								<FieldDescription>
									{description.length}/{MAX_DESCRIPTION_LENGTH}
								</FieldDescription>
							</Field>
							<Field>
								<FieldLabel htmlFor='file-request-expiration'>Accepts uploads until</FieldLabel>
								<DateInput
									earliestSelectableDate={today}
									id='file-request-expiration'
									onChange={setAcceptsUploadsUntil}
									value={acceptsUploadsUntil}
								/>
								{isDateInPast ? (
									<FieldError>Pick today or a later date.</FieldError>
								) : (
									<FieldDescription>After this date the upload link stops working.</FieldDescription>
								)}
							</Field>
							{submitError ? <FieldError>{submitError}</FieldError> : null}
						</div>
					</DialogBody>
					<DialogFooter>
						<Button disabled={createFileRequest.isPending} onClick={close} type='button' variant='secondary'>
							Cancel
						</Button>
						<Button isLoading={createFileRequest.isPending} type='submit'>
							Continue to email
						</Button>
					</DialogFooter>
				</form>
			</DialogPopup>
		</Dialog>
	);
};
